<template>
  <div
    id="app"
    @mousemove="handleTooltipMove"
    @mouseleave="hideTooltip"
  >
    <router-view/>
    <div
      v-if="tooltip.visible"
      class="hf-floating-tooltip"
      :style="tooltipStyle"
    >
      {{ tooltip.text }}
    </div>
  </div>
</template>

<script>

export default {
  name: 'App',
  data() {
    return {
      tooltip: {
        visible: false,
        text: '',
        x: 0,
        y: 0,
        placement: 'top'
      }
    }
  },
  computed: {
    tooltipStyle() {
      return {
        left: `${this.tooltip.x}px`,
        top: `${this.tooltip.y}px`,
        transform: this.tooltip.placement === 'top'
          ? 'translate(-50%, calc(-100% - 10px))'
          : 'translate(-50%, 10px)'
      }
    }
  },
  methods: {
    handleTooltipMove(event) {
      const target = event.target.closest && event.target.closest('[data-tooltip]')
      if (!target) {
        this.hideTooltip()
        return
      }

      const text = target.getAttribute('data-tooltip')
      if (!text) {
        this.hideTooltip()
        return
      }

      const rect = target.getBoundingClientRect()
      const x = Math.max(180, Math.min(window.innerWidth - 180, rect.left + rect.width / 2))
      const placement = rect.top < 88 ? 'bottom' : 'top'
      this.tooltip = {
        visible: true,
        text,
        x,
        y: placement === 'top' ? rect.top : rect.bottom,
        placement
      }
    },
    hideTooltip() {
      if (!this.tooltip.visible) return
      this.tooltip.visible = false
    }
  }
}
</script>

<style>
* {
  box-sizing: border-box;
}

html,
body,
#app {
  min-height: 100%;
}

body {
  margin: 0;
  background:
    radial-gradient(circle at 12% 10%, rgba(199, 231, 255, .95), transparent 30%),
    radial-gradient(circle at 86% 0%, rgba(235, 227, 255, .82), transparent 26%),
    linear-gradient(180deg, #fbfcff 0%, #f4f7fb 48%, #eef4f8 100%);
  color: #111827;
  scrollbar-width: thin;
  scrollbar-color: rgba(15, 23, 42, .18) transparent;
}

#app {
  font-family: Inter, Avenir, Helvetica, Arial, "PingFang SC", "Microsoft YaHei", sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #111827;
}

a {
  color: inherit;
}

::-webkit-scrollbar {
  width: 8px;
  height: 0.5em;
}

::-webkit-scrollbar-thumb {
  background-color: rgba(15, 23, 42, .16);
  border-radius: 999px;
}

::-webkit-scrollbar-track {
  background-color: transparent;
}

.glass-panel {
  border: 1px solid rgba(15, 23, 42, .08);
  background: rgba(255, 255, 255, .72);
  box-shadow: 0 18px 48px rgba(15, 23, 42, .08);
  backdrop-filter: blur(18px) saturate(1.18);
  -webkit-backdrop-filter: blur(18px) saturate(1.18);
}

.page-shell {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 18px;
  padding: 2px 2px 0;
}

.page-kicker {
  margin: 0 0 8px;
  color: #667085;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .12em;
  text-transform: uppercase;
}

.page-title {
  margin: 0;
  color: #101828;
  font-size: 28px;
  line-height: 1.2;
}

.page-subtitle {
  margin: 10px 0 0;
  color: #667085;
  font-size: 14px;
}

.panel-card {
  border-radius: 16px;
  padding: 18px;
}

.toolbar-panel {
  border-radius: 16px;
  padding: 16px 16px 4px;
}

.table-panel {
  overflow: hidden;
  border-radius: 16px;
  padding: 12px;
}

.action-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  margin: 0 0 14px;
}

.stat-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(160px, 1fr));
  gap: 12px;
}

.stat-card {
  min-height: 98px;
  padding: 16px;
  border: 1px solid rgba(15, 23, 42, .08);
  border-radius: 14px;
  background: rgba(255, 255, 255, .68);
}

.stat-label {
  margin: 0 0 10px;
  color: #667085;
  font-size: 13px;
}

.stat-value {
  color: #101828;
  font-size: 28px;
  font-weight: 800;
}

.muted-text {
  color: #667085;
}

.hf-field,
.hf-textarea,
.hf-select {
  width: 100%;
  border: 1px solid rgba(15, 23, 42, .12);
  border-radius: 12px;
  background: rgba(255, 255, 255, .78);
  color: #101828;
  box-shadow: 0 1px 2px rgba(15, 23, 42, .04);
  outline: none;
  transition: border-color .16s ease, box-shadow .16s ease, background .16s ease;
}

.hf-field,
.hf-select {
  height: 38px;
  padding: 0 12px;
}

.hf-textarea {
  min-height: 120px;
  padding: 12px;
  resize: vertical;
}

.hf-field::placeholder,
.hf-textarea::placeholder {
  color: #98a2b3;
}

.hf-field:hover,
.hf-textarea:hover,
.hf-select:hover,
.hf-field:focus,
.hf-textarea:focus,
.hf-select:focus {
  border-color: rgba(16, 24, 40, .32);
  box-shadow: 0 0 0 4px rgba(16, 24, 40, .05);
}

.hf-form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(178px, 1fr));
  gap: 12px;
}

.hf-form-item {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.hf-label {
  color: #475467;
  font-size: 13px;
  font-weight: 700;
}

.hf-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 38px;
  border: 1px solid rgba(15, 23, 42, .12);
  border-radius: 12px;
  background: rgba(255, 255, 255, .78);
  color: #101828;
  cursor: pointer;
  gap: 8px;
  padding: 0 14px;
  font-weight: 700;
  line-height: 1;
  transition: transform .14s ease, border-color .14s ease, background .14s ease, box-shadow .14s ease;
}

.hf-btn:hover {
  border-color: rgba(15, 23, 42, .22);
  background: rgba(255, 255, 255, .96);
  box-shadow: 0 8px 24px rgba(15, 23, 42, .08);
}

.hf-btn:active {
  transform: translateY(1px);
}

.hf-btn:disabled {
  cursor: not-allowed;
  opacity: .5;
  transform: none;
  box-shadow: none;
}

.hf-btn--primary {
  border-color: #101828;
  background: #101828;
  color: #ffffff;
}

.hf-btn--primary:hover {
  border-color: #344054;
  background: #344054;
  color: #ffffff;
}

.hf-btn--danger {
  border-color: rgba(180, 35, 24, .16);
  background: rgba(254, 243, 242, .9);
  color: #b42318;
}

.hf-btn--text {
  min-height: auto;
  border: 0;
  background: transparent;
  box-shadow: none;
  color: #344054;
  padding: 0;
}

.hf-btn--text:hover {
  background: transparent;
  box-shadow: none;
  color: #101828;
}

.hf-table-wrap {
  position: relative;
  overflow: auto;
  border: 1px solid rgba(15, 23, 42, .08);
  border-radius: 14px;
  background: rgba(255, 255, 255, .42);
  max-width: 100%;
}

.hf-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 760px;
  table-layout: fixed;
  color: #344054;
  font-size: 13px;
}

.hf-table th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: rgba(248, 250, 252, .86);
  color: #667085;
  font-weight: 800;
  text-align: left;
  white-space: nowrap;
}

.hf-table th,
.hf-table td {
  border-bottom: 1px solid rgba(15, 23, 42, .07);
  height: 46px;
  overflow: hidden;
  padding: 10px 12px;
  text-overflow: ellipsis;
  vertical-align: middle;
  white-space: nowrap;
}

.hf-table [data-tooltip] {
  cursor: help;
}

.hf-table tr {
  background: rgba(255, 255, 255, .62);
}

.hf-table tbody tr:hover {
  background: rgba(242, 244, 247, .92);
}

.hf-table tr:last-child td {
  border-bottom: 0;
}

.hf-table-empty {
  padding: 36px 18px;
  color: #667085;
  text-align: center;
}

.hf-loading {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  background: rgba(255, 255, 255, .62);
  backdrop-filter: blur(4px);
  color: #475467;
  font-weight: 700;
}

.hf-pagination {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  color: #475467;
  font-size: 13px;
}

.hf-page-btn {
  min-width: 34px;
  height: 34px;
  border: 1px solid rgba(15, 23, 42, .1);
  border-radius: 10px;
  background: rgba(255, 255, 255, .78);
  color: #475467;
  cursor: pointer;
}

.hf-page-btn.active {
  background: #101828;
  color: #ffffff;
}

.hf-page-btn:disabled {
  cursor: not-allowed;
  opacity: .45;
}

.hf-modal-mask {
  position: fixed;
  inset: 0;
  z-index: 40;
  display: grid;
  place-items: center;
  background: rgba(248, 250, 252, .54);
  backdrop-filter: blur(10px);
  padding: 18px;
}

.hf-modal {
  width: min(560px, 100%);
  border: 1px solid rgba(15, 23, 42, .08);
  border-radius: 18px;
  background: rgba(255, 255, 255, .9);
  box-shadow: 0 24px 80px rgba(15, 23, 42, .16);
  backdrop-filter: blur(18px);
  padding: 20px;
}

.hf-modal--wide {
  width: min(820px, 100%);
}

.hf-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.hf-modal__title {
  margin: 0;
  color: #101828;
  font-size: 18px;
}

.hf-modal__close {
  display: grid;
  width: 32px;
  height: 32px;
  place-items: center;
  border: 1px solid rgba(15, 23, 42, .08);
  border-radius: 10px;
  background: rgba(255, 255, 255, .72);
  cursor: pointer;
}

.hf-tag {
  display: inline-flex;
  align-items: center;
  border: 1px solid rgba(15, 23, 42, .08);
  border-radius: 999px;
  background: rgba(242, 244, 247, .9);
  color: #344054;
  gap: 7px;
  min-height: 26px;
  padding: 0 10px;
  font-size: 12px;
  font-weight: 700;
}

.hf-tag button {
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 0;
}

.hf-tag--success {
  border-color: rgba(18, 183, 106, .18);
  background: rgba(236, 253, 243, .9);
  color: #027a48;
}

.hf-tag--danger {
  border-color: rgba(240, 68, 56, .18);
  background: rgba(254, 243, 242, .9);
  color: #b42318;
}

.hf-floating-tooltip {
  position: fixed;
  z-index: 120;
  max-width: min(420px, calc(100vw - 32px));
  border: 1px solid rgba(15, 23, 42, .12);
  border-radius: 12px;
  background: rgba(16, 24, 40, .96);
  box-shadow: 0 18px 48px rgba(15, 23, 42, .22);
  color: #ffffff;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.55;
  overflow-wrap: anywhere;
  padding: 9px 12px;
  pointer-events: none;
  white-space: normal;
}

.hf-segmented {
  display: inline-flex;
  border: 1px solid rgba(15, 23, 42, .08);
  border-radius: 12px;
  background: rgba(255, 255, 255, .52);
  padding: 3px;
}

.hf-segmented button {
  min-height: 32px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: #101828;
  cursor: pointer;
  padding: 0 12px;
  font-weight: 700;
}

.hf-segmented button.active {
  background: #101828;
  color: #ffffff;
}

.hf-toast-stack {
  position: fixed;
  top: 18px;
  right: 18px;
  z-index: 100;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: min(340px, calc(100vw - 36px));
}

.hf-toast {
  display: grid;
  grid-template-columns: 10px 1fr;
  gap: 12px;
  border: 1px solid rgba(15, 23, 42, .08);
  border-radius: 16px;
  background: rgba(255, 255, 255, .9);
  box-shadow: 0 18px 48px rgba(15, 23, 42, .12);
  opacity: 0;
  padding: 14px;
  transform: translateY(-8px);
  transition: opacity .18s ease, transform .18s ease;
}

.hf-toast.is-visible {
  opacity: 1;
  transform: translateY(0);
}

.hf-toast__dot {
  width: 9px;
  height: 9px;
  margin-top: 5px;
  border-radius: 50%;
  background: #344054;
}

.hf-toast--success .hf-toast__dot {
  background: #12b76a;
}

.hf-toast--warning .hf-toast__dot {
  background: #f79009;
}

.hf-toast--error .hf-toast__dot {
  background: #f04438;
}

.hf-toast__title {
  color: #101828;
  font-weight: 800;
}

.hf-toast__message {
  margin-top: 3px;
  color: #667085;
  font-size: 13px;
  line-height: 1.5;
}

@media (max-width: 860px) {
  .page-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .stat-grid {
    grid-template-columns: 1fr;
  }
}
</style>
